import CmModal from "../Controls/CmModal";
import Spinner from "../Controls/Spinner";

function pick(obj, ...keys) {
  for (const k of keys) {
    if (obj?.[k] != null && obj[k] !== "") return obj[k];
  }
  return undefined;
}

function normalizeCandidate(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    Name: pick(raw, "Name", "name") || "",
    ShortName: pick(raw, "ShortName", "shortName") || "",
    Description: pick(raw, "Description", "description") || "",
    CategoryID: pick(raw, "CategoryID", "categoryID") || null,
    CategoryName: pick(raw, "CategoryName", "categoryName") || "",
    WikiUrl: pick(raw, "WikiUrl", "wikiUrl") || "",
    // Must pass through — SkillSearch.candidateToDraft uses this for the create form.
    ReleaseDate: pick(raw, "ReleaseDate", "releaseDate") || null,
    Confidence: Number(pick(raw, "Confidence", "confidence") ?? 0),
    MatchedSkillID: pick(raw, "MatchedSkillID", "matchedSkillID") || null,
    Rationale: pick(raw, "Rationale", "rationale") || "",
  };
}

function formatReleaseDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function isWebCandidate(c) {
  const rationale = (c.Rationale || "").toLowerCase();
  const wiki = (c.WikiUrl || "").trim();
  return (
    !!wiki ||
    rationale.includes("wikipedia") ||
    rationale.includes("live web") ||
    rationale.includes("fetched from")
  );
}

function isOnetCandidate(c) {
  const rationale = (c.Rationale || "").toLowerCase();
  return rationale.includes("o*net") || rationale.includes("onet");
}

/**
 * After resolve, let the user pick a suggested skill definition or create from the typed name.
 */
export default function SkillResolveModal({
  query = "",
  candidates = [],
  allowBlankCreate = true,
  busy = false,
  error = "",
  onSelect,
  onCreateBlank,
  onClose,
}) {
  const items = (Array.isArray(candidates) ? candidates : [])
    .map(normalizeCandidate)
    .filter((c) => c && (c.Name || "").trim());

  return (
    <CmModal
      open
      bare
      onClose={onClose}
      title="Which skill did you mean?"
      size="wide"
      testId="skill-resolve-modal"
      className="skill-resolve-modal"
    >
      <div className="skill-resolve" data-testid="skill-resolve">
        <p className="skill-resolve__lead">
          Looking up <strong>{query}</strong>
          {busy
            ? "…"
            : " in the catalog, O*NET, and online. Pick a match, or create it yourself if none fit."}
        </p>

        {busy ? (
          <div
            className="skill-resolve__loading"
            data-testid="skill-resolve-loading"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <Spinner show />
            <p className="fieldHint">Searching the catalog, O*NET, and the web…</p>
          </div>
        ) : null}

        {error ? (
          <p className="skill-resolve__error" role="alert" data-testid="skill-resolve-error">
            {error}
          </p>
        ) : null}

        {!busy && items.length > 0 ? (
          <ul className="skill-resolve__list" data-testid="skill-resolve-list">
            {items.map((c, idx) => {
              const key = `${c.MatchedSkillID || "new"}-${c.Name}-${idx}`;
              const isCatalog = Number(c.MatchedSkillID) > 0;
              const fromOnet = !isCatalog && isOnetCandidate(c);
              const fromWeb = !isCatalog && !fromOnet && isWebCandidate(c);
              const pct = Math.round(Math.min(1, Math.max(0, c.Confidence)) * 100);
              return (
                <li key={key}>
                  <button
                    type="button"
                    className="skill-resolve__option"
                    data-testid="skill-resolve-option"
                    onClick={() => onSelect?.(c)}
                  >
                    <div className="skill-resolve__option-head">
                      <span className="skill-resolve__name">{c.Name}</span>
                      {c.ShortName && c.ShortName !== c.Name ? (
                        <span className="skill-resolve__short">{c.ShortName}</span>
                      ) : null}
                      {isCatalog ? (
                        <span className="skill-resolve__badge">In catalog</span>
                      ) : fromOnet ? (
                        <span className="skill-resolve__badge skill-resolve__badge--onet">
                          O*NET
                        </span>
                      ) : fromWeb ? (
                        <span className="skill-resolve__badge skill-resolve__badge--web">
                          From web
                        </span>
                      ) : (
                        <span className="skill-resolve__badge skill-resolve__badge--new">
                          Suggested
                        </span>
                      )}
                      {pct > 0 ? (
                        <span className="skill-resolve__confidence">{pct}% match</span>
                      ) : null}
                    </div>
                    {c.CategoryName ? (
                      <div className="skill-resolve__meta">{c.CategoryName}</div>
                    ) : null}
                    {c.ReleaseDate ? (
                      <div className="skill-resolve__meta" data-testid="skill-resolve-release-date">
                        Released: {formatReleaseDate(c.ReleaseDate)}
                      </div>
                    ) : null}
                    <p className="skill-resolve__desc" data-testid="skill-resolve-description">
                      {(c.Description || "").trim() ||
                        `${c.Name} is a professional skill. Choose this if it matches what you meant.`}
                    </p>
                    {c.Rationale ? (
                      <p className="skill-resolve__rationale">{c.Rationale}</p>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}

        {!busy && items.length === 0 && !error ? (
          <p className="fieldHint">
            No catalog or online matches yet. You can still create this skill.
          </p>
        ) : null}

        <div className="skill-resolve__actions">
          {allowBlankCreate ? (
            <button
              type="button"
              className="skill-resolve__blank"
              data-testid="skill-resolve-blank"
              disabled={busy}
              onClick={() => onCreateBlank?.()}
            >
              None of these — create “{query}”
            </button>
          ) : null}
          <button type="button" className="skill-resolve__cancel" onClick={() => onClose?.()}>
            Cancel
          </button>
        </div>
      </div>
    </CmModal>
  );
}
